import * as React from "react";
import { MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";

export type TierList = { [tier: string]: Array<string> };
export const allTiers = ["S", "A", "B", "C", "D"];
export const emptyTierList = (): TierList => ({
  S: [],
  A: [],
  B: [],
  C: [],
  D: [],
});

export const UNIT_SIZE = 52;
export const ITEM_CLASS = "w-[52px]";
export const GAP_SIZE = 12;

export const calcDropIdx = (
  elRect: any,
  containerRect: any,
  unitSize: number,
) => {
  const rowLength = Math.floor(800 / (unitSize + GAP_SIZE));
  const rowIdx = Math.max(
    0,
    Math.floor((elRect?.top - containerRect?.top) / (UNIT_SIZE + GAP_SIZE * 2)),
    // rowLength,
  );
  const idx = Math.max(
    rowIdx * rowLength +
      Math.floor(
        ((elRect?.left ?? 9999) -
          // +50 for the tier letter's label
          35 -
          (containerRect?.left ?? 0)) /
          (unitSize + GAP_SIZE),
      ),
    0,
  );
  return idx;
};

export const transform = (idx: number, unitSize = UNIT_SIZE): string => {
  const rowLength = Math.floor(800 / (unitSize + GAP_SIZE));
  return `translate(${(idx % rowLength) * (unitSize + GAP_SIZE)}px, ${
    8 + Math.floor(idx / rowLength) * (UNIT_SIZE + 2 + GAP_SIZE * 3)
  }px)`;
};

type UseProps = {
  encodeUrl: (tl: TierList, set: number) => string;
  decodeUrl: (url: string) => [TierList, number];
  baseUrl: string;
  set: number;
  unitSize?: number;
};

export const useTierList = ({
  encodeUrl,
  decodeUrl,
  baseUrl,
  set,
  unitSize: baseUnitSize,
}: UseProps) => {
  const unitSize = baseUnitSize || UNIT_SIZE;
  const mouseSensor = useSensor(MouseSensor, {
    // Require the mouse to move by 10 pixels before activating
    activationConstraint: {
      distance: 5,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    // Press delay of 100ms, with tolerance of 5px of movement
    activationConstraint: {
      delay: 100,
      tolerance: 5,
    },
  });
  const sensors = useSensors(mouseSensor, touchSensor);

  const [shareUrl, setShareUrl] = React.useState(
    `https://tactics.tools/tier-list/${baseUrl}`,
  );

  const [tiers, setTiers] = React.useState<TierList>(() => {
    const parts = window.location.href.split("/");
    const last = parts[parts.length - 1];
    if (
      (last[0] === "A" ||
        last[0] === "B" ||
        last[0] === "C" ||
        last[0] === "D" ||
        last[0] === "E" ||
        last[0] === "F" ||
        last[0] === "G") &&
      last.length >= 2
    ) {
      const [tiers, _set] = decodeUrl(last);
      return tiers;
    } else {
      return emptyTierList();
    }
  });

  let init = React.useRef(true);
  React.useEffect(() => {
    if (init.current) {
      init.current = false;
    } else {
      setTiers(emptyTierList());
    }
  }, [set]);

  const onRemove = React.useCallback((itemId: string) => {
    setTiers((tiers) => ({
      S: tiers.S.filter((id) => id !== itemId),
      A: tiers.A.filter((id) => id !== itemId),
      B: tiers.B.filter((id) => id !== itemId),
      C: tiers.C.filter((id) => id !== itemId),
      D: tiers.D.filter((id) => id !== itemId),
    }));
  }, []);

  const [draggedId, setDraggedId] = React.useState(null);

  React.useEffect(() => {
    const qs = encodeUrl(tiers, set);
    if (qs.length < 2) {
      window.history.replaceState({}, "", `/tier-list/${baseUrl}`);
      setShareUrl(`https://tactics.tools/tier-list/${baseUrl}`);
    } else {
      window.history.replaceState({}, "", `/tier-list/${baseUrl}/` + qs);
      setShareUrl(`https://tactics.tools/tier-list/${baseUrl}/` + qs);
    }
  }, [tiers]);

  const clearTierList = React.useCallback(() => {
    setTiers(emptyTierList());
  }, []);

  const handleDragStart = React.useCallback((ev: any) => {
    setDraggedId(ev.active.id);
  }, []);

  const handleDragEnd = React.useCallback((ev: any) => {
    const unitId = ev.active.id;
    setTiers((baseTiers: TierList) => {
      const tiers = { ...baseTiers };
      for (let tier of allTiers) {
        const idx = tiers[tier].indexOf(unitId);
        if (idx != -1) {
          tiers[tier].splice(idx, 1);
        }
      }

      if (ev.over != null && tiers[ev.over.id] != null) {
        const idx = calcDropIdx(
          ev.active.rect.current.translated,
          ev.over.rect,
          unitSize,
        );

        const tier = ev.over.id;
        tiers[tier].splice(idx, 0, unitId);
        return tiers;
      } else {
        return tiers;
      }
    });
    setDraggedId(null);
  }, []);

  const tierItems = React.useMemo(
    () =>
      tiers.S.concat(tiers.A).concat(tiers.B).concat(tiers.C).concat(tiers.D),
    [tiers],
  );

  const onClick = React.useCallback((id: string) => {
    setTiers((baseTiers: TierList) => {
      const tiers = { ...baseTiers };

      tiers["D"] = [...tiers["D"], id];
      return tiers;
    });
  }, []);

  return {
    clearTierList,
    draggedId,
    handleDragEnd,
    handleDragStart,
    shareUrl,
    tiers,
    tierItems,
    onClick,
    sensors,
    onRemove,
  };
};
